import { LogHelper } from '@/helpers/Log'
import { CATEGORY_TYPE_KVG, CATEGORY_TYPE_UNKONWN } from '@/config/constants'
import { DateTimeUtil } from '@/utils/DateTime'
import cloneDeep from 'lodash/cloneDeep'
import { MissingParameterError, NotFoundError, isRequired } from '@/utils/CustomErrors'

const sortByOrder = (a, b) => (a.order >= b.order ? 1 : -1)

export const ProductUtil = {
  /**
   * Returns the categoryId of the given productId
   * @param productData   Store construct containing categories, groups and products objects
   * @param productId
   * @return {string|undefined}
   */
  getCategoryIdFromProduct(productData, productId = isRequired('productId')) {
    if (!productData?.categories) throw new MissingParameterError('categories')
    if (!productData?.groups) throw new MissingParameterError('groups')

    const { categories, groups } = productData
    if (!categories) return undefined

    return Object.keys(categories).find(categoryId =>
      categories[categoryId].groups.find(groupId => groups[groupId].products.includes(productId))
    )
  },

  /**
   * Returns the groupId of the given productId
   * @param groups
   * @param productId
   * @return {string|undefined}
   */
  getGroupIdFromProduct(groups = isRequired('groups'), productId = isRequired('productId')) {
    // TODO: Will a product always be only in one group?
    return Object.keys(groups).find(groupId => groups[groupId].products.includes(productId))
  },

  /**
   * Groups the selected products of a user by the category
   * and sorts both the products and the categories by order
   * @param productData       Store construct containing categories, groups and products objects
   * @param personProducts    Selected products of a person
   * @return []
   */
  getSelectedProductsByCategory(productData, personProducts = isRequired('products')) {
    if (!productData?.categories) throw new MissingParameterError('categories')

    const selectedProducts = ProductUtil.getSelectedProductsOfPerson(productData.products, personProducts)
    if (selectedProducts.length === 0) return selectedProducts

    const { categories } = productData

    const productsByCategory = {}
    let unknownProducts = []

    /**
     * Get order of category with given categoryId
     * If category not found, return high order number to show this category as last
     * (needed for existing customers with old products)
     * @param categoryId
     * @return {*|number}
     */
    const getCategoryOrderById = categoryId => Object.values(categories).find(c => c.id === categoryId)?.order || 1000
    const sortCategoriesByOrder = (a, b) => (getCategoryOrderById(a.id) >= getCategoryOrderById(b.id) ? 1 : -1)

    Object.values(selectedProducts).forEach(p => {
      const categoryId = ProductUtil.getCategoryIdFromProduct(productData, p.id)
      categoryId
        ? (productsByCategory[categoryId] = [...(productsByCategory[categoryId] || []), p])
        : unknownProducts.push(p)
    })

    unknownProducts.forEach(p => {
      const categoryId = Object.keys(productsByCategory).find(c =>
        productsByCategory[c].find(pc => pc?.upgrades?.includes(p.id))
      )
      if (categoryId) {
        // Upgrade products
        productsByCategory[categoryId].push(p)
      } else {
        // Old products without currently existing category (used for 'Bestandeskunden')
        productsByCategory[CATEGORY_TYPE_UNKONWN] = [...(productsByCategory[CATEGORY_TYPE_UNKONWN] || []), p]
      }
    })

    Object.keys(productsByCategory).forEach(categoryId => {
      productsByCategory[categoryId].sort(sortByOrder)
    })

    return Object.keys(productsByCategory)
      .map(categoryId => {
        return { id: categoryId, products: productsByCategory[categoryId] }
      })
      .sort(sortCategoriesByOrder)
  },

  getSelectedProductsOfPerson(allProducts = isRequired('products'), personProducts = isRequired('selected products')) {
    const mergeProduct = productId =>
      Object.assign({ id: productId }, allProducts[productId], personProducts[productId])
    const replacePrice = product => {
      product.price = product.prices.find(p => !!p.selected)
      return product
    }

    return Object.keys(personProducts)
      .map(productId => mergeProduct(productId))
      .filter(product => product.selected)
      .map(product => replacePrice(product))
  },

  /**
   * Returns all categories in an array sorted by order field
   * @param categories
   * @return []
   */
  getSortedCategories(categories = isRequired('categories')) {
    const data = []
    const categoriesCopy = cloneDeep(categories)
    Object.keys(categoriesCopy).forEach(categoryId => data.push(categoriesCopy[categoryId]))

    return data.sort(sortByOrder)
  },

  /**
   * Returns all groups of a category sorted by the order field
   * @param categories
   * @param groups
   * @param categoryId
   * @return []
   */
  getSortedGroupsFromCategory(
    categories = isRequired('categories'),
    groups = isRequired('groups'),
    categoryId = isRequired('categoryId')
  ) {
    if (!categories[categoryId]) throw new NotFoundError('category with categoryId', categoryId)

    const groupsCopy = cloneDeep(groups)

    return categories[categoryId].groups.map(groupId => groupsCopy[groupId]).sort(sortByOrder)
  },

  /**
   * Returns all existing products of a category sorted by the order field
   * @param productData
   * @param categoryId
   * @return []
   */
  getSortedProductsFromCategory(productData, categoryId = isRequired('categoryId')) {
    if (!productData?.categories) throw new MissingParameterError('categories')
    if (!productData?.groups) throw new MissingParameterError('groups')
    if (!productData?.products) throw new MissingParameterError('products')

    const { categories, groups, products } = productData

    if (!categories[categoryId]) throw new NotFoundError('category with categoryId', categoryId)

    return categories[categoryId].groups
      .reduce((acc, groupId) => acc.concat(ProductUtil.getSortedProductsFromGroup(groups, products, groupId)), [])
      .sort(sortByOrder)
  },

  /**
   * Returns all existing products of a group sorted by the order field
   * @param groups
   * @param products
   * @param groupId
   * @return []
   */
  getSortedProductsFromGroup(
    groups = isRequired('groups'),
    products = isRequired('products'),
    groupId = isRequired('groupId')
  ) {
    if (!groups[groupId]) throw new NotFoundError('group with groupId', groupId)

    const productsCopy = cloneDeep(products)
    return groups[groupId].products
      .map(productId => productsCopy[productId])
      .filter(product => product)
      .sort(sortByOrder)
  },

  hasAccident(product) {
    // eslint-disable-next-line no-prototype-builtins
    return product.prices.find(p => p.selected)?.hasOwnProperty('toggle')
  },

  hasFranchise(product) {
    // Return true if the product.price has a property 'value'
    // eslint-disable-next-line no-prototype-builtins
    return product.prices.find(p => p.selected)?.hasOwnProperty('value')
  },

  hasAccidentToggle(product, categoryId, contractStartDate, dateOfBirth) {
    if (!ProductUtil.hasAccident(product)) return false
    // FLEX-1303 Age at the end of year of contract start date < 15 -> accident always true for KVG and toggle hidden
    const ageEndOfContractStartYear = DateTimeUtil.getAgeEndOfContractStartYear(dateOfBirth, contractStartDate)
    return categoryId !== CATEGORY_TYPE_KVG || ageEndOfContractStartYear >= 15
  },

  /**
   * format input as currency/number
   * @param  {String|Number} input
   * @param  {String} currency
   * @param  {Number} fixed
   * @return {String}
   */
  formatCurrency(input, currency = 'CHF', fixed = 2) {
    let formatted = ''
    const number = parseFloat(input)
    if (Number.isNaN(number)) {
      return formatted
    }

    let options = {
      minimumFractionDigits: fixed,
      maximumFractionDigits: fixed,
    }

    if (currency) {
      options = {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'code',
      }
    }

    try {
      formatted = new Intl.NumberFormat('de-CH', options).format(number)
    } catch (error) {
      LogHelper.error(`Couldn't format currency: ${error}`)
    }
    return formatted
  },

  transformProductMessages(messages) {
    // TODO: Parameter checking: key !== undefined, message !== undefined and isString
    return messages.map(message => {
      return {
        key: message.key,
        type: message.type.toLowerCase(),
      }
    })
  },
  transformDoctorForProduct(doctor) {
    // TODO: parameter checking?
    const {
      firstName,
      lastName,
      zsrNumber,
      email,
      phoneNumber,
      faxNumber,
      medicalOfficeName,
      medicalOfficeStatus,
      medicalOfficeNets,
      ...values
    } = doctor
    const { geo, ...address } = values

    return {
      firstName,
      lastName,
      zsrNumber,
      email,
      phoneNumber,
      faxNumber,
      medicalOfficeName,
      medicalOfficeStatus,
      medicalOfficeNets,
      address: {
        ...address,
        ...geo,
      },
    }
  },
}
